/**
 * Civil-date arithmetic on "YYYY-MM-DD" strings.
 *
 * Implemented from scratch rather than via `Date` because `packages/core` reads no clock
 * and no environment (C-13). A local gym day is already a plain civil date by the time it
 * reaches here — see D-12 — so every operation below is exact integer arithmetic with no
 * timezone, no DST, and no instant anywhere in it.
 *
 * Resolving "what is today in Asia/Kolkata" is the caller's job, and belongs where `Intl`
 * is allowed. Core only ever receives the answer.
 *
 * The conversions use Howard Hinnant's days-from-civil algorithm, which is exact for all
 * dates in the proleptic Gregorian calendar.
 */
/** Days since 1970-01-01. Negative before it. */
export function toEpochDay(date) {
    const [y, m, d] = date.split("-").map(Number);
    const year = m <= 2 ? y - 1 : y;
    const era = Math.floor(year / 400);
    const yoe = year - era * 400; // [0, 399]
    const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1; // [0, 365]
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; // [0, 146096]
    return era * 146097 + doe - 719468;
}
/** The inverse of `toEpochDay`. */
export function fromEpochDay(epochDay) {
    const z = epochDay + 719468;
    const era = Math.floor(z / 146097);
    const doe = z - era * 146097; // [0, 146096]
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
    const y = yoe + era * 400;
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100)); // [0, 365]
    const mp = Math.floor((5 * doy + 2) / 153); // [0, 11]
    const d = doy - Math.floor((153 * mp + 2) / 5) + 1; // [1, 31]
    const m = mp + (mp < 10 ? 3 : -9); // [1, 12]
    return format(m <= 2 ? y + 1 : y, m, d);
}
function format(year, month, day) {
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
/** `date` shifted by whole days. Negative goes back. */
export function addDays(date, days) {
    return fromEpochDay(toEpochDay(date) + days);
}
/** The day before `date`. What the shorthand `y` flag means. */
export function yesterday(date) {
    return addDays(date, -1);
}
/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date) {
    // 1970-01-01 (epoch day 0) was a Thursday.
    const dow = (toEpochDay(date) + 4) % 7;
    return dow < 0 ? dow + 7 : dow;
}
/**
 * The Monday of `date`'s week (D-35). Weeks are Monday-to-Sunday, and a week is named by
 * its Monday, so a 6M bucket label is itself a valid date string.
 */
export function startOfWeek(date) {
    const dow = dayOfWeek(date);
    return addDays(date, -((dow + 6) % 7));
}
/**
 * The first day of `date`'s month — the inclusive start of the This-month range.
 *
 * A calendar month rather than thirty days back, deliberately: a range labelled "this
 * month" whose total does not match a calendar is worse than a chart that is sparse for
 * the first few days of one.
 */
export function startOfMonth(date) {
    return `${date.slice(0, 7)}-01`;
}
/** "YYYY-MM" — the bucket key for the All range. */
export function monthKey(date) {
    return date.slice(0, 7);
}
/**
 * `date` shifted by whole calendar months, clamping to the end of the shorter month:
 * three months back from the 31st of May is the 28th (or 29th) of February, not the 3rd
 * of March. Clamping is what keeps a range window from silently including an extra day.
 */
export function addMonths(date, months) {
    const [y, m, d] = date.split("-").map(Number);
    const zeroBased = y * 12 + (m - 1) + months;
    const year = Math.floor(zeroBased / 12);
    const month = zeroBased - year * 12 + 1;
    return format(year, month, Math.min(d, daysInMonth(year, month)));
}
export function daysInMonth(year, month) {
    if (month === 2) {
        const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        return leap ? 29 : 28;
    }
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
/** Whole days from `from` to `to`. Negative when `to` precedes `from`. */
export function daysBetween(from, to) {
    return toEpochDay(to) - toEpochDay(from);
}
//# sourceMappingURL=dates.js.map